// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import React, { useEffect, useRef, useState } from 'react'

import { employersEmployees } from 'rails_routes'
import { useWindowLocation } from 'utils/windowLocation'

import AirDatepickerReact from './reusable/AirDatepickerReact'

function OffboardEmployeeDialog ({ closeDialog, employerName, employee }: any) {
  const { assign } = useWindowLocation()

  const dialogRef = useRef(null)
  const [error, setError] = useState<null | string>(null)
  const [employeeEmail, setEmployeeEmail] = useState('')
  const [effectiveDate, setEffectiveDate] = useState(new Date())
  const [isDisabled, setIsDisabled] = useState<boolean>(false)

  useEffect(() => {
    if (employee) {
      (dialogRef.current as any)?.classList.remove('hidden')
      setEmployeeEmail(employee.email)
      if (employee.end_date) {
        setEffectiveDate(new Date(employee.end_date))
      }
      window.document.body.classList.add('overflow-hidden')
    } else {
      window.document.body.classList.remove('overflow-hidden')
      if ((dialogRef.current as any)?.open) {
        (dialogRef.current as any)?.classList.add('hidden')
      }
    }
  }, [employee])

  const handleSubmit = async (event: any) => {
    setIsDisabled(true)
    event.preventDefault()

    const formData = new FormData(event.target)

    if (!formData.get('effective_date')) {
      setError('Effective date of Loss of Benefits required!')
      setIsDisabled(false)
      return
    }

    const params: { [key: string]: any } = { }
    formData.forEach((value, key) => { params[key] = value })

    setEffectiveDate(new Date(formData.get('effective_date')!.toString()))
    // TODO: this method of making an API call is deprecated -- please use reactquery instead
    // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
    const request = new FetchRequest(
      'post',
      event.target.action,
      {
        body: JSON.stringify(params),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    )
    const response = await request.perform()
    const json = await response.json
    if (response.ok) {
      assign(json.redirect_to)
    } else {
      setError(json.error)
      setIsDisabled(false)
    }
  }

  if (!employee) {
    return <div />
  }

  return (
    <div ref={dialogRef} className='hidden fixed inset-0 w-full h-full z-40 overflow-y-auto rounded bg-gray-400 bg-opacity-60 p-0 sm:mx-auto shadow-xl'>
      <div className='p-8 mt-12 mb-12 border-b max-w-3xl mx-4 justify-self-center bg-white rounded'>
        <div className='flex justify-between mb-2 border-b gap-2'>
          <div className='flex flex-col'>
            <h2 className='h2'>Offboard Employee</h2>
            <div>
              <p className='mt-1 text-sm text-gray-500 mb-4'>{employee.first_name + ' ' + employee.last_name}</p>
            </div>
          </div>
          <div className='flex flex-col space-y-4 items-end text-sm text-thatch-gray-secondary'>
            <button onClick={closeDialog}>Close</button>
          </div>
        </div>
        <div>
          <form onSubmit={handleSubmit} action={employersEmployees.terminate()} acceptCharset='UTF-8' method='post'>
            <div className='mt-6 grid grid-cols-1 gap-y-6 gap-x-2'>
              <label className='block text-sm text-gray-700 font-semibold' htmlFor='cobra_eligible'>Employer Subject to Federal COBRA Regulations</label>
              <div className='flex space-x-3'>
                <div>
                  <input name='cobra_eligible' type='hidden' value='0' autoComplete='off' />
                  <input className='checkbox-default' type='checkbox' value='1' name='cobra_eligible' id='cobra_eligible' />
                </div>
                <div>
                  <div className='text-sm'>By checking this box, I attest that {employerName} had at least 20 employees on most typical business days during {new Date().getFullYear() - 1}.</div>
                  <p className='text-xs text-gray-400 italic my-2'>
                    Thatch will send COBRA communications to eligible terminated employees, including a COBRA general notice, FAQ, and an option to accept or waive the COBRA offer.
                  </p>
                </div>
              </div>
              <div>
                <label className='block text-sm text-gray-700 mb-1 font-semibold' htmlFor='effective_date'>Effective Date of Loss of Benefits</label>
                <AirDatepickerReact id='effective_date' options={{ dateFormat: 'MMMM d, yyyy', minDate: new Date().setFullYear(new Date().getFullYear() - 1), maxDate: new Date().setFullYear(new Date().getFullYear() + 1), autoClose: true, selectedDates: [effectiveDate] }} />
                <p className='text-xs text-gray-400 italic my-2'>
                  This is the employee&apos;s final day of eligibility for benefits with {employerName}
                  {' '}The employee will typically stop receiving reimbursements for purchases made in connection with the {employerName} ICHRA after the date of loss of benefits, unless {employerName} is subject to federal COBRA regulations and the employee elects to participate in COBRA.
                </p>
                <p className='text-xs text-gray-400 italic my-2'>
                  Note that most individual plans continue health insurance coverage through the end of the month regardless of employment termination.
                </p>
              </div>

              <div>
                <label className='block text-sm text-gray-700 mb-1 font-semibold' htmlFor='employee_personal_email'>Personal Email Address</label>
                <input className='input-default' required type='text' value={employeeEmail} onChange={(e) => setEmployeeEmail(e.target.value)} name='employee_personal_email' id='employee_personal_email' />
                <div id='employee_termination_email_validation_error' />
                <p className='text-xs text-gray-400 italic my-2'>Please provide the employee&apos;s personal email address so that we can send communications related to health coverage and COBRA, if applicable.</p>
              </div>

              <input type='hidden' name='employee_id' id='employee_id' value={employee.id} autoComplete='off' />
            </div>
            <div className='mt-6'>
              <input type='submit' data-testid='offboard_input' disabled={isDisabled} name='commit' value='Offboard employee' className='w-full inline-flex justify-center rounded-md hover:cursor-pointer border border-transparent bg-red-600 py-2 px-4 text-sm text-white shadow-sm hover:bg-red-700 hover:border-red-600 focus:outline-none focus:ring-0' />
            </div>
            {error &&
              <div data-testid='error_message' className='bg-red-100 border border-red-400 text-red-700 p-2 my-2 rounded relative text-center text-xs' role='alert'>
                {error}
              </div>}
            <div />
            <div className='bg-gray-100 border border-gray-400 text-gray-700 p-2 my-2 rounded relative' role='alert'>
              <div className='text-xs italic alert alert-danger'>
                On the effective date of loss of benefits, Thatch will contact the offboarded employee to communicate their healthcare options going forward.
                If the effective date of loss of benefits has already passed, Thatch will reach out immediately.
                Employees of employers subject to federal COBRA will be offered options (i) to elect COBRA and continue receiving reimbursements under their ICHRA, (ii) to decline COBRA and pay for their own individual coverage premiums through Thatch, or (iii) to close their Thatch account. Employees of employers that are not subject to federal COBRA will be offered options (ii) and (iii) only.
              </div>
            </div>
            <div className='bg-yellow-100 border border-yellow-400 text-yellow-700 p-2 my-2 rounded relative' role='alert'>
              <div className='text-xs italic alert alert-danger'>
                The sooner {employerName} informs Thatch of an employee&apos;s loss of benefits, the more we can do to prevent unnecessary premium payments.
                Insurance premium payment timing varies by insurance carrier. Usually, insurance carriers charge premiums in advance of the following month.
                In these cases, {employerName} is billed for premiums already paid to the insurance carrier.
                Thatch will try to cancel the plan and obtain a refund from the carrier. If Thatch receives a refund, those funds will be credited to your company&apos;s healthcare spend.
                Thatch will prevent payments for future month premiums but cannot guarantee that carriers will refund payments that have already been made.
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
export default OffboardEmployeeDialog
